import type { PageNode } from "@tento-ui/brand/page-model";

import { arrayProp, objectEntry, stringField } from "./node-props";

// Which platform's one-liner the page is showing, and how it decided.
//
// This module holds the parts with no DOM in them — the detection, the
// validation, the shared selection — and `tsh-platform-bar.ts` holds the
// control that draws them. The split is not architectural taste: `content.ts`
// needs the `InstallPlatform` shape, `content.deno.test.ts` imports
// `content.ts`, and a Deno test that reaches a `@component` decorator is
// pulling a custom-element registry into a process that has no document. The
// bar re-exports everything here, so a reader who only knows the component's
// path still gets the same names from it.

export type PlatformId = "macos" | "linux" | "windows";

/** One row of the install table: what to call it, and what to run. */
export type InstallPlatform = {
  id: PlatformId;
  label: string;
  /** The hardware/shell qualifier under the label — "x86_64", "PowerShell". */
  detail: string;
  /** The shell's prompt, drawn faint before the command: `$`, `PS>`. */
  prompt: string;
  command: string;
};

export const PLATFORM_IDS: PlatformId[] = ["macos", "linux", "windows"];

/** The platform a reader gets when nothing else is known. */
export const DEFAULT_PLATFORM: PlatformId = "macos";

const STORAGE_KEY = "tsh.install.platform";

export function isPlatformId(value: string): value is PlatformId {
  return PLATFORM_IDS.some((id) => id === value);
}

/**
 * The hints a browser can offer about the machine, all optional.
 *
 * Optional AND nullable on purpose: `navigator.userAgentData` is absent on
 * Safari and Firefox, `?.platform` is `undefined` there, and a caller reading
 * a stored value hands over `null`. Accepting both is what keeps every call
 * site from writing the same `?? undefined` dance.
 */
export interface PlatformHints {
  /** `navigator.userAgentData?.platform` — "macOS", "Windows", "Linux". */
  uaPlatform?: Option<string>;
  /** The legacy `navigator.platform` — "MacIntel", "Win32", "Linux x86_64". */
  platform?: Option<string>;
  userAgent?: Option<string>;
}

/**
 * Read a platform off the browser's own description of itself.
 *
 * Pure, and taking its input as an argument rather than reading `navigator`,
 * because the whole risk here is a regex: `/Win/` against a Linux UA string,
 * `/Mac/` against an iPad that claims to be a Macintosh. Those are answerable
 * by a test only if the inputs can be handed in.
 *
 * `userAgentData.platform` is asked first because it is the value a browser
 * still maintains — the legacy `navigator.platform` is frozen for
 * compatibility and `userAgent` is a museum of other people's browsers. The
 * legacy pair is the fallback rather than the source, and both are searched
 * together: `navigator.platform` alone is empty on some embedded webviews.
 *
 * A wrong guess is cheap — the reader clicks the platform they are on — so
 * this prefers answering to being sure, and macOS is the default because it is
 * the platform tsh shipped first.
 */
export function detectPlatform(hints: PlatformHints): PlatformId {
  const ua = hints.uaPlatform ?? "";
  if (ua !== "") {
    if (/mac/i.test(ua)) return "macos";
    if (/win/i.test(ua)) return "windows";
    // "Chrome OS" is the value Chromium sends, spelled out with a space —
    // matching only /cros/ here reads a Chromebook as a Mac whenever the
    // legacy fields are missing. ChromeOS installs the Linux build.
    if (/linux|cros|chrome os|chromium os/i.test(ua)) return "linux";
  }

  const legacy = `${hints.platform ?? ""} ${hints.userAgent ?? ""}`;
  // iPadOS reports "Macintosh" in its UA, which is the right answer for this
  // page anyway: nothing here installs on an iPad, and macOS is the closest
  // thing to what that reader will run the command on later.
  if (/Mac|iPhone|iPad/.test(legacy)) return "macos";
  if (/Win/.test(legacy)) return "windows";
  if (/Linux|X11|CrOS/.test(legacy)) return "linux";

  return DEFAULT_PLATFORM;
}

/** The hints this browser offers, or none at all when there is no browser. */
function navigatorHints(): PlatformHints {
  if (typeof navigator === "undefined") return {};
  // `userAgentData` is not in the DOM lib yet. Narrowed to the one field read
  // rather than widened to `any`, so a typo in `platform` is still an error.
  const nav = navigator as Navigator & { userAgentData?: { platform?: string } };
  return {
    uaPlatform: nav.userAgentData?.platform ?? null,
    platform: nav.platform,
    userAgent: nav.userAgent,
  };
}

/**
 * A previous choice, if this browser kept one and it still names a platform we
 * ship. Validated rather than trusted: the key is public, it survives a
 * release that drops a platform, and an unknown id would select no tab at all.
 */
function storedPlatform(): Option<PlatformId> {
  try {
    // Safari in private mode, a sandboxed frame and a Deno process without
    // `--location` all throw on the ACCESS, not on the read, so the `typeof`
    // guard and the getter both sit inside the try.
    if (typeof localStorage === "undefined") return null;
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw !== null && isPlatformId(raw) ? raw : null;
  } catch {
    return null;
  }
}

function persistPlatform(id: PlatformId): void {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(STORAGE_KEY, id);
  } catch {
    // A reader who cannot be remembered still gets a working page.
  }
}

/**
 * What the page should show before anyone clicks: the reader's last choice if
 * they made one, otherwise a guess from the browser.
 *
 * The stored choice wins over detection deliberately. Someone who selected
 * Linux on a Mac is reading the page for a machine they are not sitting at,
 * and the detector has no way to learn that.
 */
export function initialPlatform(): PlatformId {
  return storedPlatform() ?? detectPlatform(navigatorHints());
}

type Listener = (id: PlatformId) => void;

let current: Option<PlatformId> = null;
const listeners = new Set<Listener>();

/**
 * The page's one selected platform.
 *
 * A module singleton rather than a prop threaded through the nodes, because
 * the hero's bar and the CTA's bar are two components with no ancestor in
 * common — the route appends both to `#app` — and a reader who picks Windows
 * at the top and scrolls to a macOS command at the bottom has been told two
 * different things about the same install.
 *
 * Initialized on first `get()` rather than at module load: reading `navigator`
 * and `localStorage` while a bundle is still evaluating is what makes a module
 * unimportable from a test process, and this one is imported by `content.ts`.
 */
export const platformStore = {
  get(): PlatformId {
    current ??= initialPlatform();
    return current;
  },

  set(id: PlatformId): void {
    if (platformStore.get() === id) return;
    current = id;
    persistPlatform(id);
    for (const listener of listeners) listener(id);
  },

  /** Subscribe, and get back the unsubscribe. */
  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
};

/**
 * The install table off a node's prop bag, dropping anything malformed.
 *
 * Same rule as `node-props.ts`: a prop of the wrong shape reads as absent, not
 * as a half-built row. A tab labelled `undefined` that copies an empty command
 * is worse than a page with no tabs, and the sections fall back to the node's
 * flat `command` when this comes back empty.
 */
export function platformsProp(node: PageNode): InstallPlatform[] {
  const out: InstallPlatform[] = [];
  for (const value of arrayProp(node, "platforms")) {
    const entry = objectEntry(value);
    if (!entry) continue;
    const id = stringField(entry, "id");
    const label = stringField(entry, "label");
    const command = stringField(entry, "command");
    if (!id || !isPlatformId(id) || !label || !command) continue;
    out.push({
      id,
      label,
      detail: stringField(entry, "detail") ?? "",
      prompt: stringField(entry, "prompt") ?? "$",
      command,
    });
  }
  return out;
}
